import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { captureDeviceAcceptanceProbe } from "./deviceAcceptanceService";

export interface ProductionDeviceRouterPolicy {
  schema_version: 1;
  contract_id: "production-device-router-v1-manual-gated-approved-engines";
  enabled: boolean;
  minimum_samples: number;
  minimum_accuracy: number;
  approved_engines: Record<string,string[]>;
  updated_at?: string|null;
}
export interface ProductionDeviceRouteDecision {
  status: "DISABLED"|"FALLBACK"|"ROUTED";
  enabled: boolean;
  automatic: true;
  candidate_promotion_allowed: false;
  fallback: "canonical_production";
  reason: string;
  routes: Record<string,{engine:string|null;reason:string;production_approved:boolean}>;
  data_plane?: boolean;
  production_semantics_changed?: false;
  policy_updated_at?: string|null;
  active_bundle_id?: string|null;
  production_pipeline_sha256?: string|null;
  device_instance_id?: string|null;
}
export async function fetchProductionDeviceRouter():Promise<{policy:ProductionDeviceRouterPolicy;device_acceptance_status:string;automatic_routing_default:false}>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/production-device-router",{credentials:"include",cache:"no-store",headers:{Accept:"application/json"}},15_000);
  if(!response.ok)throw new Error(`Production-Geräterouter konnte nicht geladen werden (${response.status}).`);
  return response.json();
}
export async function saveProductionDeviceRouterPolicy(patch:Partial<ProductionDeviceRouterPolicy>):Promise<ProductionDeviceRouterPolicy>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/production-device-router",{method:"PUT",credentials:"include",headers:{"Content-Type":"application/json",Accept:"application/json"},body:JSON.stringify(patch)},15_000);
  if(!response.ok){const payload=await response.json().catch(()=>({})) as {detail?:string};throw new Error(payload.detail||`Production-Geräterouter konnte nicht gespeichert werden (${response.status}).`);}return response.json();
}
export async function evaluateCurrentProductionDeviceRoute():Promise<ProductionDeviceRouteDecision>{
  const response=await fetchWithTimeout("/api/admin/vision-optimizer/production-device-router/decision",{method:"POST",credentials:"include",headers:{"Content-Type":"application/json",Accept:"application/json"},body:JSON.stringify({probe:captureDeviceAcceptanceProbe()})},15_000);
  if(!response.ok)throw new Error(`Production-Geräterouting konnte nicht bewertet werden (${response.status}).`);return response.json();
}


const PRODUCTION_ROUTE_CACHE_KEY="materialentnahme:production-device-route:v1";
let routeRefresh:Promise<ProductionDeviceRouteDecision|null>|null=null;

function canonicalFallbackDecision(reason="offline_or_unavailable"):ProductionDeviceRouteDecision{
  return {status:"FALLBACK",enabled:false,automatic:true,candidate_promotion_allowed:false,fallback:"canonical_production",reason,routes:{},data_plane:true,production_semantics_changed:false};
}

function routeMatchesCurrentDevice(value:ProductionDeviceRouteDecision):boolean{
  const current=captureDeviceAcceptanceProbe();
  return !value.device_instance_id||value.device_instance_id===current.device_instance_id;
}

export function readCachedProductionDeviceRoute():ProductionDeviceRouteDecision|null{
  try{
    const raw=localStorage.getItem(PRODUCTION_ROUTE_CACHE_KEY);if(!raw)return null;
    const parsed=JSON.parse(raw) as ProductionDeviceRouteDecision;
    if(!parsed||parsed.data_plane!==true||parsed.production_semantics_changed!==false||!routeMatchesCurrentDevice(parsed))return null;
    return parsed;
  }catch{return null;}
}

export async function refreshProductionDeviceRoute():Promise<ProductionDeviceRouteDecision|null>{
  if(routeRefresh)return routeRefresh;
  routeRefresh=(async()=>{
    try{
      const response=await fetchWithTimeout("/api/visual-lab/production-device-route",{method:"POST",credentials:"include",cache:"no-store",headers:{"Content-Type":"application/json",Accept:"application/json"},body:JSON.stringify({probe:captureDeviceAcceptanceProbe()})},1_500);
      if(!response.ok)return null;
      const value=await response.json() as ProductionDeviceRouteDecision;
      if(value.data_plane!==true||value.production_semantics_changed!==false||!routeMatchesCurrentDevice(value))return null;
      localStorage.setItem(PRODUCTION_ROUTE_CACHE_KEY,JSON.stringify(value));
      return value;
    }catch{return null;}
    finally{routeRefresh=null;}
  })();
  return routeRefresh;
}

export function productionDeviceRouteLocalFirst():ProductionDeviceRouteDecision{
  const cached=readCachedProductionDeviceRoute();
  void refreshProductionDeviceRoute();
  return cached??canonicalFallbackDecision();
}
